import { useWalkPlaceholderPositions } from "@/hooks/use-walk-placeholder-positions";
import { useAppSelector } from "@/store/store-hooks";
import {
  IElementGenericImgSheet,
  IElementImg360,
  IElementOdometryPath,
  isIElementOdometryPath,
  type IElementGenericOdometryPath,
} from "@faro-lotv/ielement-types";
import { isEqual } from "es-toolkit";
import { Vector3 } from "three";
import { selectTrajectoryWaypointPanoramas } from "./path-selectors";
import { useOdometryPathWalkPositions } from "./use-odometry-path-positions";

type WalkPathPoints = {
  /** The odometry path, if the trajectory is stored in an odometry file */
  odometryFilePath: IElementOdometryPath | undefined;
  /** The panoramas grouped by the path */
  panos: IElementImg360[];
  /** The trajectory loaded from the odometry file, empty if there is none */
  filePoints: Vector3[];
  /** The points of the trajectory at the elevation used in walk mode */
  points: Vector3[];
};

/**
 * @returns the points of a path trajectory at the elevation used in walk mode
 * @param odometricPath the path to compute the trajectory of
 * @param sheetForElevation sheet whose elevation is used to place the trajectory
 */
export function useWalkPathPoints(
  odometricPath: IElementGenericOdometryPath,
  sheetForElevation: IElementGenericImgSheet | undefined,
): WalkPathPoints {
  // A path either groups the panoramas it was reconstructed from, or references an odometry file
  const odometryFilePath = isIElementOdometryPath(odometricPath)
    ? odometricPath
    : undefined;

  const panos = useAppSelector(
    selectTrajectoryWaypointPanoramas(odometricPath),
    isEqual,
  );

  const panoPoints = useWalkPlaceholderPositions(panos, sheetForElevation);

  const filePoints = useOdometryPathWalkPositions(
    odometryFilePath,
    sheetForElevation,
  );

  return {
    odometryFilePath,
    panos,
    filePoints,
    points: odometryFilePath ? filePoints : panoPoints,
  };
}
